import React, { useMemo } from "react";

import { BROKER_ALERT_DESC_CLASS } from "@/components/Broker/brokerAlertUi";
import { cn } from "@/lib/utils";
import type { CustomCsvWizardState } from "../../../types/customCsvImport";
import { groupCsvImportWarnings } from "../../../utils/customCsvImportWarnings";
import FifoMatchingIllustration from "./visuals/FifoMatchingIllustration";
import GroupedWarningAlert from "./GroupedWarningAlert";

export interface CustomCsvWarningAckStepProps {
  state: CustomCsvWizardState;
  onStateChange: (patch: Partial<CustomCsvWizardState>) => void;
}

const CustomCsvWarningAckStep = ({
  state,
  onStateChange,
}: CustomCsvWarningAckStepProps): React.ReactElement => {
  const warningGroups = useMemo(
    () => groupCsvImportWarnings(state.preview?.warnings ?? []),
    [state.preview?.warnings]
  );

  const toggleMany = (codes: string[], checked: boolean) => {
    const next = new Set(state.acknowledgedWarningCodes);
    for (const code of codes) {
      if (checked) next.add(code);
      else next.delete(code);
    }
    onStateChange({ acknowledgedWarningCodes: next });
  };

  const groupElements = warningGroups.map((group) => {
    if (group.kind === "fifo_pairing") {
      return (
        <GroupedWarningAlert
          key={`fifo-${group.warnings.map((w) => w.code).join("-")}`}
          warnings={group.warnings}
          acknowledged={state.acknowledgedWarningCodes}
          onToggle={toggleMany}
          illustration={<FifoMatchingIllustration />}
        />
      );
    }
    if (group.kind === "pnl_configuration") {
      return (
        <GroupedWarningAlert
          key={`pnl-${group.warnings.map((w) => w.code).join("-")}`}
          warnings={group.warnings}
          acknowledged={state.acknowledgedWarningCodes}
          onToggle={toggleMany}
        />
      );
    }
    return (
      <GroupedWarningAlert
        key={group.warning.code}
        warnings={[group.warning]}
        acknowledged={state.acknowledgedWarningCodes}
        onToggle={toggleMany}
      />
    );
  });

  return (
    <div className="flex flex-col gap-4" data-custom-csv-warning-ack-step="">
      <p className={cn(BROKER_ALERT_DESC_CLASS, "m-0")}>
        Confirm you understand these warnings before importing.
      </p>
      {groupElements}
    </div>
  );
};

export default CustomCsvWarningAckStep;
